import { useEffect } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import { useWellCardPanelStore } from "stores/wellCardPanel";

import { useNormalizationUnitCorrection } from "hooks/useNormalizationUnitCorrection";

import { useChartSettings } from "components/multiphase-chart/context";
import { buildNormalizeByInput } from "components/multiphase-chart/util/buildNormalizeByInput";

const selectGlobalNormalizeBy = (state: RootState) => state.normalizeBy.globalNormalizeBy;

/**
 * Hands the card chart's normalize input to the Production Array, which sits
 * outside the chart's settings provider. Only the derived input crosses over:
 * lifting the provider instead would re-render the whole panel on every chart
 * setting change.
 */
export function usePublishChartNormalizeBy(): void {
  const [chartSettings] = useChartSettings();
  const globalNormalizeBy = useSelector(selectGlobalNormalizeBy);
  const local = useNormalizationUnitCorrection(chartSettings.settings?.normalizeBy);
  const global = useNormalizationUnitCorrection(globalNormalizeBy);
  const useNormalizeBy = chartSettings.settings?.useNormalizeBy ?? false;
  const setChartNormalizeBy = useWellCardPanelStore((s) => s.setChartNormalizeBy);

  useEffect(() => {
    setChartNormalizeBy(
      useNormalizeBy ? buildNormalizeByInput(true, local, global) : null
    );
  }, [useNormalizeBy, local, global, setChartNormalizeBy]);

  // An unmounted chart normalizes nothing, and a value left behind would be
  // sent for the next well before its chart has said otherwise.
  useEffect(() => () => setChartNormalizeBy(null), [setChartNormalizeBy]);
}
